import {callAPI} from '@/store/utils';

export default {
  namespaced: true,
  state: {
    cardsList: [],
    companyAccounts: [],
    currentCard: null,
    budgetDetails: null,
  },
  mutations: {
    setCards(state, res) {
      state.cardsList = res?.result || res || [];
    },
    setCurrentCard(state, res) {
      state.currentCard = res?.result || res || null;
    },
    setCompanyAccounts(state, res) {
      state.companyAccounts = res?.result || res || null;
    },
    setBudgetDetails(state, res) {
      state.budgetDetails = res?.result || res || [];
    },
  },
  actions: {
    async actionGetCardsList({commit, rootState}) {
      const canViewAll = Boolean(
        rootState.user?.permissions?.manage_all_payment_cards,
      );
      const query = canViewAll ? '?view=all' : '';
      const res = await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards${query}`,
      );
      commit('setCards', res);
      return res;
    },
    async actionGetCardById({commit, rootState}, cardId) {
      const res = await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards/${cardId}`,
      );
      commit('setCurrentCard', res);
      return res;
    },
    async actionCreateCard({rootState}, params) {
      // TT-6732: 'individual_card' (personal) or 'budget_card' (shared). The
      // selected person is the cardholder for an individual card, or the
      // notification main user for a budget/shared card.
      const cardType = params.card_type || 'individual_card';
      const par = {
        card_type: cardType,
        company_id: rootState.companyId,
        name: params.name,
      };
      if (cardType === 'budget_card') {
        par.notified_user_id = params.user_id;
        // PROVISIONAL: the create endpoint does not accept a spending limit yet
        // (limits go through the spending-limits endpoints). Sent so the backend
        // can pick it up once supported; ignored otherwise.
        if (params.spending_limit != null && params.spending_limit !== '') {
          par.spending_limit = Number(params.spending_limit);
        }
      } else {
        par.user_id = params.user_id;
      }

      // TT-6778: card creation is OTP-protected on the backend. Forward the
      // OTP challenge id + entered code for both card types.
      if (params.otp_id) par.otp_id = params.otp_id;
      if (params.code) par.code = params.code;

      return await callAPI('POST', `equals/cards`, {...par});
    },
    // TT-6732 — update the notified user (validator) of a shared/budget card.
    async actionUpdateCardNotifiedUser({rootState}, {cardId, userId}) {
      return await callAPI(
        'PATCH',
        `equals/cards/${cardId}/notified-user`,
        {company_id: rootState.companyId, notified_user_id: userId},
      );
    },
    async actionChangeCardStatus({rootState}, params) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };

      return await callAPI(
        'POST',
        `equals/cards/${params.cardId}/transition`,
        payload,
      );
    },
    async actionReplaceCard({rootState}, params) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };

      return await callAPI(
        'POST',
        `equals/cards/${params.cardId}/replace`,
        payload,
      );
    },
    async actionConvertToPhysicalCard({rootState}, params) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };

      return await callAPI(
        'POST',
        `equals/cards/${params.cardId}/convert-to-physical`,
        payload,
      );
    },
    async actionSetPin({rootState}, params) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };
      return await callAPI('PUT', `equals/cards/${params.cardId}/pin`, payload);
    },
    async actionGetClientAccessToken(
      {rootState},
      {cardId, purpose, code = null, otp_id = null},
    ) {
      const payload = {
        purpose,
        ...(code ? {code} : {}),
        ...(otp_id ? {otp_id} : {}),
      };

      return await callAPI(
        'POST',
        `company/${rootState.companyId}/equals/cards/${cardId}/access-token`,
        payload,
      );
    },
    async actionPost3ds({rootState}, params = {}) {
      const {company_id, ...rest} = params;
      const payload = {
        ...rest,
        // TT-6787: a 3DS challenge can belong to any company the validator
        // manages — not only the currently selected one. Target the operation's
        // own company so the backend can locate the challenge; fall back to the
        // selected company for single-company validators.
        company_id: company_id ?? rootState.companyId,
      };
      const res = await callAPI('POST', `equals/3ds-validation`, payload);
      return res;
    },
    async actionGetCardTransactions({rootState}, params = {}) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };

      return await callAPI(
        'POST',
        `equals/cards/${params.cardId}/transactions`,
        payload,
      );
    },
    async actionGetCardSpendingLimits({rootState}, cardId) {
      return await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards/${cardId}/spending-limits`,
      );
    },
    async actionUpdateCardSpendingLimit(
      {rootState},
      {cardId, cardLimitId, amount, limit_window, active},
    ) {
      const payload = {
        amount,
        ...(limit_window ? {limit_window} : {}),
        ...(typeof active === 'boolean' ? {active} : {}),
        company_id: rootState.companyId,
      };
      return await callAPI(
        'PUT',
        `company/${rootState.companyId}/equals/cards/${cardId}/spending-limits/${cardLimitId}`,
        payload,
      );
    },
    async actionGetCardTransactionLimits({rootState}, cardId) {
      return await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards/${cardId}/transaction-limits`,
      );
    },
    async actionUpdateCardTransactionLimit(
      {rootState},
      {cardId, cardLimitId, amount, active},
    ) {
      const payload = {
        amount,
        ...(typeof active === 'boolean' ? {active} : {}),
        company_id: rootState.companyId,
      };
      return await callAPI(
        'PUT',
        `company/${rootState.companyId}/equals/cards/${cardId}/transaction-limits/${cardLimitId}`,
        payload,
      );
    },
  },
};
